import type { TableColumnsType } from 'ant-design-vue'
import { formatPayStatus, payStatusList } from '../newOrderList/components/useFilterHeader'
import { formatShipStatus, shipStatusList } from '../newOrderList/shipStatus'
import type { IosRefundEvidenceFilters, IosRefundEvidenceRecord } from './types'

export interface StatusTagDisplay {
  label: string
  color: string
  backgroundColor: string
}

type StatusTagStyle = Omit<StatusTagDisplay, 'label'>

export const createDefaultFilters = (): IosRefundEvidenceFilters => ({
  dbtId: '',
  orderNo: '',
  messageId: '',
  timeRange: [],
  status: undefined,
})

const STATUS_TAG_FALLBACK: StatusTagDisplay = {
  label: '--',
  color: '#00000073',
  backgroundColor: '#f5f5f5',
}

export const evidenceStatusOptions = [
  { label: '未举证', value: 0, color: '#595959', backgroundColor: '#f5f5f5' },
  { label: '已举证', value: 1, color: '#1677ff', backgroundColor: '#e6f4ff' },
]

const payStatusTagStyles = createStatusStyleMap(payStatusList, [
  { color: '#595959', backgroundColor: '#f5f5f5' },
  { color: '#cf1322', backgroundColor: '#fff1f0' },
  { color: '#389e0d', backgroundColor: '#f6ffed' },
  { color: '#d48806', backgroundColor: '#fffbe6' }
])

const shipStatusTagStyles = createStatusStyleMap(shipStatusList, [
  { color: '#595959', backgroundColor: '#f5f5f5' },
  { color: '#1677ff', backgroundColor: '#e6f4ff' },
  { color: '#d48806', backgroundColor: '#fffbe6' },
  { color: '#13a8a8', backgroundColor: '#e6fffb' }
])

export function getEvidenceStatusDisplay(status?: string | number | null): StatusTagDisplay {
  if (status === undefined || status === null || status === '') {
    return STATUS_TAG_FALLBACK
  }
  const n = typeof status === 'number' ? status : Number(status)
  const found = evidenceStatusOptions.find((o) => o.value === n)
  if (found) {
    return found
  }
  return STATUS_TAG_FALLBACK
}

export function getShipStatusDisplay(status?: string | number | null) {
  return formatShipStatus(status)
}

export function getPayStatusDisplay(status?: string | number | null) {
  return formatPayStatus(status)
}

export function getPayStatusTag(status?: string | number | null): StatusTagDisplay {
  return getStatusTagByOptions(status, payStatusList, payStatusTagStyles, formatPayStatus)
}

export function getShipStatusTag(status?: string | number | null): StatusTagDisplay {
  return getStatusTagByOptions(status, shipStatusList, shipStatusTagStyles, formatShipStatus)
}

function getStatusTagByOptions(
  status: string | number | null | undefined,
  options: Array<{ label: string; value: string | null }>,
  styleMap: Record<string, StatusTagStyle>,
  formatter: (value: unknown) => string,
): StatusTagDisplay {
  if (status === undefined || status === null || status === '') {
    return STATUS_TAG_FALLBACK
  }
  const s = String(status)
  const found = options.find((o) => o.value !== null && String(o.value) === s)
  if (found) {
    return {
      label: found.label,
      ...(styleMap[s] ?? STATUS_TAG_FALLBACK),
    }
  }
  return { ...STATUS_TAG_FALLBACK, label: formatter(status) }
}

function createStatusStyleMap(
  options: Array<{ value: string | null }>,
  styles: StatusTagStyle[],
): Record<string, StatusTagStyle> {
  return options.reduce<Record<string, StatusTagStyle>>((acc, option) => {
    if (option.value === null) {
      return acc
    }
    const style = styles[Object.keys(acc).length]
    if (style) {
      acc[String(option.value)] = style
    }
    return acc
  }, {})
}

export function getEvidenceStatusTag(status?: string | number | null): StatusTagDisplay {
  return getEvidenceStatusDisplay(status)
}

export const tableColumns: TableColumnsType<IosRefundEvidenceRecord> = [
  { title: '交易 ID', dataIndex: 'messageId', key: 'messageId' },
  { title: '订单号', dataIndex: 'orderNo', key: 'orderNo' },
  { title: '产品名称', dataIndex: 'dbtId', key: 'dbtId' },
  { title: '商品名称', dataIndex: 'prodId', key: 'prodId' },
  { title: '币种', dataIndex: 'prodPriceCurrency', key: 'prodPriceCurrency' },
  { title: '商品金额', dataIndex: 'prodPrice', key: 'prodPrice' },
  { title: '订单状态', dataIndex: 'payStatus', key: 'payStatus' },
  { title: '道具状态', dataIndex: 'shipStatus', key: 'shipStatus' },
  { title: '举证状态', dataIndex: 'status', key: 'status' },
  { title: '举证时间', dataIndex: 'submitTime', key: 'submitTime' },
]
